import { useEffect, useContext } from 'react';
import { useLevels, useShopItems, useWebSocket, useAvatarsListing, useContentful, useItemsListing } from '../hooks';
import { BroadsiderHud, LoadingSpinner } from '../components';
import { AppContext } from '../providers/AppContext';
import { LevelProvider } from '../providers/LevelProvider';
import { AuthContext } from '../providers/AuthProvider';
import { DiscordSdkContext } from '../providers/DiscordSdkProvider';
import { useWalletClient } from 'wagmi';
import useIsMobile from '../hooks/useIsMobile';
import { useNavigate } from "react-router-dom";
import { IS_STANDALONE } from '../constants';
import { WebSocketProvider } from '../providers/WebSocketContext';
import { FigureModel, AssetModel } from '../models';
import { parseEarnings } from '../utils';
import { readMessage } from '../providers/BasejumpApiProvider';
import { useEarningsStore } from '../store/gameEarnings.store';
import useInterfaceScaling from '../hooks/useInterfaceScaling';

const NoBroadsider = () => {
  return (<div>You have 0 Broadsiders!</div>);
};

function AccountInternal({ accountAddress, basejumpToken }) {
  const { isDiscord } = useContext(DiscordSdkContext);
  const { homepageSelectedAvatarId, setSyncResults, setErrorData, selectedBroadsider, setSelectedBroadsider, ownedBroadsiders, walletAddress, setOwnedBroadsiders, setMetaverseItems, setPromoItems, promoItems, setInventoryItems, inventory } = useContext(AppContext);
  const { isLoading, avatars: items, fetchAvatars } = useAvatarsListing(basejumpToken, isDiscord);
  const { isLoading: dataIsLoading, metaverseItems, promo } = useContentful();
  const { data: walletClient } = useWalletClient();
  const { isLoading: inventoryIsLoading, items: assets, fetchItems } = useItemsListing(selectedBroadsider?.getAvatarId(), isDiscord, basejumpToken);
  const { isConnected: wsIsConnected, lastMessage } = useWebSocket();
  const { fetchShopItems } = useShopItems();
  const { fetchLevels, levels } = useLevels();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { reloadUser } = useContext(AuthContext);
  const addToEarnings = useEarningsStore((state) => state.addToEarnings);
  const setMaxLevel = useEarningsStore((state) => state.setMaxLevel);
  const interfaceScale = useInterfaceScaling();

  const noFiguresToShow = !isLoading && items.length < 1 && !selectedBroadsider;

  useEffect(() => {
    if (levels) {
      setMaxLevel(levels.length - 1);
    }
  }, [levels]);

  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.messageid) {
        readMessage(basejumpToken, lastMessage.messageid);
      }
      switch (lastMessage.type) {
        case 'sync_complete':
          const syncResults = {
            addedItems: (lastMessage.data?.added_items || []).map(i => AssetModel.fromBasejumpEquipment(i, isDiscord)),
            addedAvatars: (lastMessage.data?.added_avatars || []).map(i => FigureModel.fromBasejumpAvatar(i, isDiscord)),
            removedItems: (lastMessage.data?.removed_items || []).map(i => AssetModel.fromBasejumpEquipment(i, isDiscord)),
            removedAvatars: (lastMessage.data?.removed_avatars || []).map(i => FigureModel.fromBasejumpAvatar(i, isDiscord)),
          };
          if (syncResults.addedItems.length > 0 || syncResults.addedAvatars.length > 0 || syncResults.removedItems.length > 0 || syncResults.addedItems.length > 0) {
            setSyncResults(syncResults);
            if (syncResults.addedItems.length > 0 || syncResults.removedItems.length > 0) {
              fetchItems(true);
            }
            if (syncResults.addedAvatars.length > 0 || syncResults.removedAvatars.length > 0) {
              fetchAvatars(true);
            }
          }
          break;
        case 'stats_updated':
        case 'item_consumed':
          fetchAvatars(true);
          break;
        case 'order_complete':
        case 'level_complete':
          console.log('level_complete', lastMessage);
          const earnings = parseEarnings(lastMessage, isDiscord);
	        addToEarnings(earnings);
          //setEarnings(earnings);
          setErrorData(null);
          // Re-fetch inventory silently
          fetchItems(true);
          fetchShopItems();
          fetchLevels();
          fetchAvatars(true);
          reloadUser();
          break;
        default:
          console.error(JSON.stringify(lastMessage));
          break;
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    if (items.length > 0) {
      let broadsiderToSelect = items[0];
      if (selectedBroadsider) {
        const broadsiderFromList = items.find(i => i.getAvatarId() === selectedBroadsider.getAvatarId());
        if (broadsiderFromList) {
          broadsiderToSelect = broadsiderFromList; 
        }
      } else if (homepageSelectedAvatarId) {
        const broadsiderFromList = items.find(i => i.getId() === homepageSelectedAvatarId);
        if (broadsiderFromList) {
          broadsiderToSelect = broadsiderFromList; 
        }
      }
      setSelectedBroadsider(broadsiderToSelect);
    }
    setOwnedBroadsiders(items);
  }, [items, homepageSelectedAvatarId]);

  useEffect(() => {
    if (isMobile && !IS_STANDALONE) {
      navigate('/home');
    }
  }, [isMobile]);

  useEffect(() => {
    setMetaverseItems(metaverseItems);
    setPromoItems(promo);
  }, [metaverseItems.length, promo.length]);

  useEffect(() => {
    setInventoryItems(assets);
  }, [JSON.stringify(assets)]);

  useEffect(() => {
    const bodyEl = window.document.body;
    bodyEl.style.setProperty('--app-scale', interfaceScale);
  }, [interfaceScale])

  const loading = isLoading || dataIsLoading || inventoryIsLoading;
  return (
    <div className='page'>
      {loading && <LoadingSpinner />}
      {noFiguresToShow && <NoBroadsider />}
      {(!loading && selectedBroadsider) && <BroadsiderHud
        figure={selectedBroadsider}
        promoOptions={promoItems}
        inventory={inventory}
        metaverseData={metaverseItems}/>}
    </div>
  );
}

export default function Account({ accountAddress }) {
  const { basejumpToken, user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user && !IS_STANDALONE) {
      navigate('/home');
    }
  }, [user]);

  return (
    <WebSocketProvider basejumpToken={basejumpToken}>
      <LevelProvider>
        <AccountInternal accountAddress={accountAddress} basejumpToken={basejumpToken}/>
      </LevelProvider>
    </WebSocketProvider>
  );
}
